import { useEffect, useRef, useState } from 'react'

import { useMixPanel } from '@client-portal-common/Mixpanel/useMixPanel.tsx'
import { CaseContextProvider } from '@client-portal-contexts/CaseContext'
import { ClientProvider } from '@client-portal-contexts/ClientContext'
import type { DemographicsResponse } from '@client-portal-contexts/DemographicsContext'
import { DemographicsContext } from '@client-portal-contexts/DemographicsContext'
import type { LandRegistryTitleResponse } from '@client-portal-contexts/LandRegistryTitleContext'
import { LandRegistryTitleContext } from '@client-portal-contexts/LandRegistryTitleContext'
import { LoginErrorsProvider } from '@client-portal-contexts/LoginErrorsContext'
import { PropertyCaseContext } from '@client-portal-contexts/PropertyCaseContext.tsx'
import type { PropertyResponse } from '@client-portal-contexts/PropertyContext'
import { PropertyContext } from '@client-portal-contexts/PropertyContext'
import { useDynamicTheme } from '@client-portal-utils/hooks/useDynamicTheme'
import type { Theme } from '@mui/material'
import { CssBaseline } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import enGB from 'date-fns/locale/en-GB'
import { isNil } from 'lodash'
import Smartlook from 'smartlook-client'

import config from '@acre/config'
import {
  FieldDisabledProvider,
  FullPageLoadingSpinner,
  MobileErrorBoundary,
  ThemeProvider,
  UserTracker,
} from '@acre/design-system'
import {
  omitTypename,
  useGetCaseUserLazyQuery,
  useGetClientUserQuery,
  useGetClientVerificationsQuery,
  useGetOrganisationQuery,
  useGetPropertyCpQuery,
} from '@acre/graphql'
import {
  calculateCurrentOrgFlags,
  calculateFlags,
  ErrorProvider,
  Feature,
  FlagProvider,
  FlagType,
  setStorage,
  shareSessionStorage,
} from '@acre/utils'

import { areFieldsDisabled } from './App.helpers'
import ClientPortalRouter from './bootstrap/ClientPortalRouter'
import { loadData } from './pages/Properties/helpers/PropertyInfo.helpers'
import { scheduleAppointment } from './pages/shared/constants'
import { inputGlobalStyles } from './index.styles'

// @ts-expect-error - Cypress is a global variable.
const shouldDisableSmartlook = window.Cypress || import.meta.env.VITEST || navigator.webdriver

const App = shareSessionStorage(() => {
  setStorage(sessionStorage, 'authenticated', 'false').catch((err) => {
    ErrorProvider.showError(err)
  })

  // calling this to initialise mixpanel so that we can start tracking page view event.
  useMixPanel()
  const [propertyState, setPropertyState] = useState<PropertyResponse>()
  const [demographicsState, setDemographicsState] = useState<DemographicsResponse>()
  const [landRegistryState, setLandRegistryState] = useState<LandRegistryTitleResponse>()

  const { loading: loadingClient, data } = useGetClientUserQuery({
    skip: window.location.pathname.endsWith(scheduleAppointment),
  })

  const caseId = sessionStorage.getItem('case_id')
  const [loadCaseData, { data: caseData, loading: loadingCase }] = useGetCaseUserLazyQuery()

  const hasIdentified = useRef(false)

  useEffect(() => {
    if (caseId) {
      loadCaseData({ variables: { id: caseId! } })
    }
  }, [caseId, loadCaseData])

  const { data: propertyData } = useGetPropertyCpQuery({
    variables: {
      id: caseData?.caseUser.details.new_property_cp?.property_id!,
    },
    skip: isNil(caseData?.caseUser.details?.new_property_cp?.property_id),
    fetchPolicy: 'cache-first',
  })

  useEffect(() => {
    if (!propertyState?.property) {
      loadData(propertyData, setPropertyState, setDemographicsState, caseData?.caseUser?.id, setLandRegistryState)
    }
  }, [
    propertyState?.property,
    propertyData,
    setPropertyState,
    setDemographicsState,
    caseData?.caseUser?.id,
    setLandRegistryState,
  ])

  const state = propertyState ?? {}

  const { data: clientVerifications, loading: verificationsLoading } = useGetClientVerificationsQuery({
    variables: {
      id: data?.clientUser?.id!,
      useClientApi: true,
      show_pass: true,
    },
    skip: !data?.clientUser?.id,
    fetchPolicy: 'network-only',
  })

  const clientContext = data?.clientUser && {
    ...omitTypename(data?.clientUser),
    verifications:
      !verificationsLoading &&
      clientVerifications &&
      clientVerifications.clientVerifications &&
      (omitTypename(clientVerifications.clientVerifications) as ClientVerification),
  }

  const { data: clientOriginatorOrgData, loading: clientOriginatorOrgDataLoading } = useGetOrganisationQuery({
    variables: { id: data?.clientUser?.details?.client_originator!, useClientApi: true },
    skip: !data?.clientUser?.details?.client_originator,
  })

  const clientOriginatorFlagsWithoutCascade = calculateCurrentOrgFlags(clientOriginatorOrgData?.organisation)
  const clientOriginatorFlags = calculateFlags(clientOriginatorOrgData?.organisation)

  const dynamicTheme = useDynamicTheme({
    caseId,
    clientId: data?.clientUser?.id,
  })

  // Check Smartlook flag independently (removed Dashboard3 dependency)
  const isSmartlookFlagSet = clientOriginatorFlagsWithoutCascade[FlagType.Feature][Feature.SmartlookCP]

  useEffect(() => {
    if (shouldDisableSmartlook || !isSmartlookFlagSet) {
      return
    } else {
      Smartlook.init(config.SMARTLOOK_CP_KEY, {
        region: config.SMARTLOOK_REGION as 'eu' | 'us' | undefined,
        advancedNetwork: {
          allowedUrls: [
            new RegExp(`^${config.API_BASE_URL}(/.*)?$`),
            new RegExp(`^${config.CLIENT_PORTAL_API_BASE_URL}(/.*)?$`),
          ],
        },
      })
    }
  }, [isSmartlookFlagSet])

  useEffect(() => {
    if (!shouldDisableSmartlook && isSmartlookFlagSet && data?.clientUser?.id && !hasIdentified.current) {
      Smartlook.identify(data.clientUser.id, {
        contact_details_email: data.clientUser.details.contact_details_email ?? '',
        client_id: data.clientUser.id,
      })
      hasIdentified.current = true
    }
  }, [isSmartlookFlagSet, data?.clientUser])

  if (loadingClient || loadingCase || clientOriginatorOrgDataLoading) {
    return (
      <ThemeProvider theme={dynamicTheme}>
        <CssBaseline>
          {inputGlobalStyles(dynamicTheme as Theme)}
          <FullPageLoadingSpinner />
        </CssBaseline>
      </ThemeProvider>
    )
  }

  return (
    <LocalizationProvider adapterLocale={enGB} dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={dynamicTheme}>
        <CssBaseline>
          {inputGlobalStyles(dynamicTheme as Theme)}
          <MobileErrorBoundary>
            <FlagProvider value={clientOriginatorFlags}>
              <FieldDisabledProvider value={areFieldsDisabled(caseData?.caseUser.details.status as CaseStatus)}>
                <CaseContextProvider value={caseData?.caseUser as CaseVersion}>
                  <ClientProvider value={clientContext || null}>
                    <PropertyCaseContext.Provider
                      value={caseData?.caseUser?.details.new_property_cp?.details as Property}
                    >
                      <PropertyContext.Provider value={state}>
                        <DemographicsContext.Provider value={demographicsState}>
                          <LandRegistryTitleContext.Provider value={landRegistryState}>
                            <UserTracker user={data?.clientUser || null}>
                              <LoginErrorsProvider>
                                <ClientPortalRouter />
                              </LoginErrorsProvider>
                            </UserTracker>
                          </LandRegistryTitleContext.Provider>
                        </DemographicsContext.Provider>
                      </PropertyContext.Provider>
                    </PropertyCaseContext.Provider>
                  </ClientProvider>
                </CaseContextProvider>
              </FieldDisabledProvider>
            </FlagProvider>
          </MobileErrorBoundary>
        </CssBaseline>
      </ThemeProvider>
    </LocalizationProvider>
  )
})

export default App
